/**
 * Google Analytics & Google Ads Tracking Service
 * 
 * GA4 Measurement ID: G-96ZENCZVXD
 * Google Ads ID: AW-11050866829
 * 
 * gtag.js is loaded in index.html with send_page_view: false
 * This service handles all manual page views and event tracking for the SPA
 */

const GA_MEASUREMENT_ID = 'G-96ZENCZVXD';
const GA_MEASUREMENT_ID_2 = 'G-22QEHXWK4G';
const GA_MEASUREMENT_IDS = [GA_MEASUREMENT_ID, GA_MEASUREMENT_ID_2];
const GOOGLE_ADS_ID = 'AW-11050866829';
const GOOGLE_ADS_CONVERSION_LABEL = 'EnJwCKaY8f4bEI2xu5Up';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Check if we're on the production domain
const isProductionDomain = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === '2aelectronics.com' || host === 'www.2aelectronics.com';
};

// Check if GA is available (loaded by index.html on production domain only)
const isGAAvailable = () => {
  return isProductionDomain() && typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Initialize Google Analytics
 * gtag is already loaded in index.html, this just ensures config is correct
 * Only runs on production domain (2aelectronics.com)
 */
export const initGA = () => {
  if (typeof window === 'undefined' || !isProductionDomain()) return;
  
  // gtag is loaded in index.html, just verify it exists
  if (!window.gtag) {
    // Fallback: create gtag if index.html somehow didn't load it
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
    window.gtag('config', GA_MEASUREMENT_ID_2, { send_page_view: false });
    window.gtag('config', GOOGLE_ADS_ID);
  }
};

/**
 * Track a page view (called on every SPA route change)
 */
export const trackPageView = (path, title = document.title) => {
  // Track to Google Analytics if available
  if (isGAAvailable()) {
    GA_MEASUREMENT_IDS.forEach((id) => {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
        send_to: id
      });
    });
  }
  
  // Always track to backend for admin dashboard analytics
  trackBackendVisit(path);
};

/**
 * Track visit to backend API (for Security Center / Website Analytics)
 */
const trackBackendVisit = (page) => {
  try {
    // Skip tracking for admin pages
    if (page?.startsWith('/admin')) return;
    
    fetch(`${BACKEND_URL}/api/track-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: page || '/',
        referrer: document.referrer || null,
        user_agent: navigator.userAgent || null
      }),
      keepalive: true
    }).catch(() => {}); // Fire and forget
  } catch (e) {
    // Silent fail - tracking should never break the app
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGAAvailable()) return;
  window.gtag('event', eventName, params);
};

// ============================================
// CONVERSION TRACKING (Google Ads)
// ============================================

/**
 * Track Google Ads conversion - fires when user completes a valuable action
 */
export const trackGoogleAdsConversion = (value = 1.0, currency = 'USD', label = null) => {
  if (!isGAAvailable()) return;
  
  window.gtag('event', 'conversion', {
    'send_to': `${GOOGLE_ADS_ID}/${label || GOOGLE_ADS_CONVERSION_LABEL}`,
    'value': value,
    'currency': currency
  });
};

/**
 * Track repair quote submission (key conversion)
 */
export const trackRepairQuoteConversion = (repairType, estimatedValue = 1.0) => {
  trackGoogleAdsConversion(estimatedValue, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: estimatedValue,
    event_category: 'Conversions',
    repair_type: repairType
  });
};

/**
 * Track sell device submission (key conversion)
 */
export const trackSellDeviceConversion = (deviceType, quoteValue = 1.0) => {
  trackGoogleAdsConversion(quoteValue, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: quoteValue,
    event_category: 'Conversions',
    device_type: deviceType
  });
};

/**
 * Track contact form submission (key conversion)
 */
export const trackContactFormConversion = () => {
  trackGoogleAdsConversion(1.0, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 1.0,
    event_category: 'Conversions',
    form_name: 'contact_form'
  });
};

/**
 * Track appointment booking (key conversion)
 */
export const trackAppointmentConversion = (serviceType) => {
  trackGoogleAdsConversion(1.0, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 1.0,
    event_category: 'Conversions',
    service_type: serviceType
  });
};

/**
 * Track phone call click (key conversion for Google Ads)
 */
export const trackPhoneCallConversion = (source = 'website') => {
  if (!isGAAvailable()) return;
  
  // Track as Google Ads conversion
  trackGoogleAdsConversion(5.0, 'USD');
  
  // Track as GA4 event
  trackEvent('contact', {
    method: 'phone',
    event_category: 'Conversions',
    source: source
  });

  // Mirror to Meta Pixel if present
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'Contact', { method: 'phone', source });
  }
};

/**
 * Track "Get Directions" / map click (store-visit intent, key local conversion)
 */
export const trackDirectionsClick = (source = 'website') => {
  if (!isGAAvailable()) return;

  // Treat a directions click as a soft Google Ads conversion (store-visit intent)
  trackGoogleAdsConversion(3.0, 'USD');

  // GA4 event
  trackEvent('get_directions', {
    method: 'maps',
    event_category: 'Conversions',
    source: source
  });

  // Mirror to Meta Pixel if present
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'FindLocation', { source });
  }
};

/**
 * Track repair flow submission (when user submits repair request)
 */
export const trackRepairFlowConversion = (deviceType, repairType, estimatedPrice = 0) => {
  trackGoogleAdsConversion(estimatedPrice || 1.0, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: estimatedPrice || 1.0,
    event_category: 'Conversions',
    device_type: deviceType,
    repair_type: repairType
  });
};

/**
 * Track mail-in repair submission
 */
export const trackMailInConversion = (deviceType) => {
  trackGoogleAdsConversion(1.0, 'USD');
  trackEvent('generate_lead', {
    currency: 'USD',
    value: 1.0,
    event_category: 'Conversions',
    form_name: 'mail_in_repair',
    device_type: deviceType
  });
};

/**
 * Track Instant Estimate quote request (sell or repair), key micro-conversion.
 * Fired the moment the customer asks for a price, the highest-intent moment
 * before an actual lead submission.
 */
export const trackInstantEstimateRequested = (kind = 'sell', deviceQuery = '') => {
  if (!isGAAvailable()) return;

  // Soft Google Ads conversion, a quote request is strong purchase intent
  trackGoogleAdsConversion(2.0, 'USD');

  // GA4 event
  trackEvent('instant_estimate_requested', {
    event_category: 'Conversions',
    estimate_type: kind,
    device_query: (deviceQuery || '').slice(0, 100)
  });

  // Mirror to Meta Pixel if present
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', { content_category: `instant_estimate_${kind}` });
  }
};

// ============================================
// ENGAGEMENT EVENTS (GA4)
// ============================================

export const trackRepairBooked = (deviceType, repairType, source = 'website') => {
  trackEvent('repair_booked', {
    device_type: deviceType,
    repair_type: repairType,
    source: source,
    event_category: 'Repairs'
  });
};

export const trackRepairCompleted = (deviceType, repairType, ticketId) => {
  trackEvent('repair_completed', {
    device_type: deviceType,
    repair_type: repairType,
    ticket_id: ticketId,
    event_category: 'Repairs'
  });
};

export const trackQuickSale = (itemCount, totalAmount) => {
  trackEvent('quick_sale_completed', {
    item_count: itemCount,
    total_amount: totalAmount,
    event_category: 'Sales'
  });
};

export const trackDevicePurchase = (deviceType, condition, purchasePrice) => {
  trackEvent('device_purchased', {
    device_type: deviceType,
    condition: condition,
    purchase_price: purchasePrice,
    event_category: 'Purchases'
  });
};

export const trackInventoryAdded = (itemName, category, quantity) => {
  trackEvent('inventory_added', {
    item_name: itemName,
    category: category,
    quantity: quantity,
    event_category: 'Inventory'
  });
};

export const trackAIAssistantUsed = (prompt, itemsGenerated) => {
  trackEvent('ai_assistant_used', {
    prompt_length: prompt?.length || 0,
    items_generated: itemsGenerated,
    event_category: 'AI Features'
  });
};

export const trackCustomerCreated = (source = 'manual') => {
  trackEvent('customer_created', {
    source: source,
    event_category: 'Customers'
  });
};

export const trackAdminLogin = (username) => {
  trackEvent('admin_login', {
    username: username,
    event_category: 'Admin'
  });
};

export const trackTabChange = (tabName, section = 'store') => {
  trackEvent('tab_change', {
    tab_name: tabName,
    section: section,
    event_category: 'Navigation'
  });
};

export const trackSMSCampaignSent = (campaignName, recipientCount, templateId) => {
  trackEvent('sms_campaign_sent', {
    campaign_name: campaignName,
    recipient_count: recipientCount,
    template_id: templateId,
    event_category: 'SMS Marketing'
  });
};

export const trackSMSTemplateSelected = (templateId, templateName, category) => {
  trackEvent('sms_template_selected', {
    template_id: templateId,
    template_name: templateName,
    category: category,
    event_category: 'SMS Marketing'
  });
};

export const trackChatBotInteraction = (messageType, category = null) => {
  trackEvent('chatbot_interaction', {
    message_type: messageType,
    category: category,
    event_category: 'ChatBot'
  });
};

export const trackChatBotLead = (name, phone, repairType) => {
  trackEvent('chatbot_lead', {
    has_name: !!name,
    has_phone: !!phone,
    repair_type: repairType,
    event_category: 'ChatBot'
  });
};

export const trackAddToCart = (itemId, itemName, price) => {
  trackEvent('add_to_cart', {
    item_id: itemId,
    item_name: itemName,
    price: price,
    event_category: 'E-commerce'
  });
};

export const trackPurchase = (orderId, totalAmount, items) => {
  trackEvent('purchase', {
    transaction_id: orderId,
    value: totalAmount,
    items: items,
    event_category: 'E-commerce'
  });
};

export const trackFormStart = (formName) => {
  trackEvent('form_start', {
    form_name: formName,
    event_category: 'Engagement'
  });
};

export const trackFormSubmit = (formName, success = true) => {
  trackEvent('form_submit', {
    form_name: formName,
    success: success,
    event_category: 'Engagement'
  });
};

export const trackOutboundLink = (url) => {
  trackEvent('outbound_link', {
    url: url,
    event_category: 'Engagement'
  });
};

export const getGAMeasurementId = () => GA_MEASUREMENT_ID;
export const isAnalyticsEnabled = () => true;
